import { Link, createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
	CalendarDays,
	Check,
	Clock,
	HelpCircle,
	MapPin,
	Plus,
	StarsIcon,
	User,
	X,
} from "lucide-react";
import { useState } from "react";
import { Button, buttonVariants } from "~/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { eventApi } from "~/lib/api/event";
import { useAuth } from "~/lib/hooks/useAuth";
import { useUserStore } from "~/lib/stores/user";
import { cn, formatLocation } from "~/lib/utils";

function HomeComponent() {
	const [activeTab, setActiveTab] = useState<"upcoming" | "hosting" | "past">(
		"upcoming",
	);
	const { logout } = useAuth();
	const user = useUserStore((state) => state.user);
	const { events } = Route.useLoaderData();
	const hasEvents = events.upcoming.length > 0 || events.hosting.length > 0;

	// Format date and time from ISO string
	const formatDate = (dateString: string): string => {
		const date = new Date(dateString);
		return new Intl.DateTimeFormat("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		}).format(date);
	};

	const formatTime = (dateString: string): string => {
		const date = new Date(dateString);
		return new Intl.DateTimeFormat("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		}).format(date);
	};

	return (
		<div className="min-h-screen bg-black text-primary-foreground">
			{/* Header */}
			<header className="sticky top-0 z-10 border-b border-primary/20 bg-black/90 backdrop-blur-xl">
				<div className="mx-auto max-w-5xl flex items-center justify-between p-4">
					<h1 className="text-3xl font-bold tracking-wide font-heading">
						<span className="text-accent">m</span>
						<span className="text-primary-foreground">eteor</span>
					</h1>
					<div className="flex items-center gap-3">
						<Link
							to="/event/create"
							className={cn(
								buttonVariants({ variant: "outline", size: "sm" }),
								"hidden md:flex rounded-full border-accent/30 bg-accent/10 text-accent hover:bg-accent/20 hover:text-accent",
							)}
						>
							<Plus className="h-4 w-4" />
							Create
						</Link>
						<div className="flex items-center gap-2">
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant="ghost"
										size="icon"
										className="rounded-full text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary/10"
									>
										<User className="h-5 w-5" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									align="end"
									className="bg-black border-primary/20"
								>
									<DropdownMenuItem
										className="text-primary-foreground/70 focus:text-primary-foreground focus:bg-primary/10"
										asChild
									>
										<Link to={`/u/${user?.username}`}>Profile</Link>
									</DropdownMenuItem>
									<DropdownMenuItem
										className="text-primary-foreground/70 focus:text-primary-foreground focus:bg-primary/10"
										onClick={async () => await logout()}
									>
										Logout
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
					</div>
				</div>
			</header>

			{/* Main Content */}
			<main className="mx-auto max-w-5xl p-4 pt-6 pb-24 md:pb-6">
				{/* Welcome Message */}
				<div className="mb-8">
					<h2 className="text-4xl md:text-5xl font-bold text-primary-foreground bg-gradient-to-br from-white via-white/90 to-primary-foreground/80 bg-clip-text flex items-center gap-2 flex-wrap-reverse md:flex-nowrap font-heading">
						Welcome back <span className="text-accent">{user?.firstName}!</span>
						<StarsIcon className="w-6 h-6 text-muted-light hidden md:block" />
					</h2>
					<p className="mt-2 text-lg text-secondary">
						{hasEvents ? (
							"Here's what's coming up for you"
						) : (
							<span>
								You don't have any upcoming events right now.{" "}
								<Button
									asChild
									variant="link"
									className="h-auto p-0 text-accent hover:text-accent/90 font-medium"
								>
									<Link to="/event/create">Create one?</Link>
								</Button>
							</span>
						)}
					</p>
				</div>

				{/* Tabs */}
				<Tabs
					defaultValue="upcoming"
					value={activeTab}
					onValueChange={(value) => setActiveTab(value as typeof activeTab)}
					className="mb-6"
				>
					<TabsList className="bg-primary/10 border border-primary/20">
						<TabsTrigger
							value="upcoming"
							className="text-primary-foreground/70 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
						>
							Upcoming{" "}
							{events.upcoming.length > 0 && `(${events.upcoming.length})`}
						</TabsTrigger>
						<TabsTrigger
							value="hosting"
							className="text-primary-foreground/70 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
						>
							Hosting
						</TabsTrigger>
						<TabsTrigger
							value="past"
							className="text-primary-foreground/70 data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
						>
							Past
						</TabsTrigger>
					</TabsList>
				</Tabs>

				{/* Events Grid - Always show with create card */}
				<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-2">
					{/* Create Event Card - Always visible */}
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
					>
						<Link
							to="/event/create"
							className="group relative overflow-hidden rounded-3xl bg-primary/10 backdrop-blur-xl border border-primary/20 hover:border-accent/30 transition-all duration-300 cursor-pointer block"
						>
							<div className="relative aspect-[5/3] w-full flex flex-col items-center justify-center p-6 text-center">
								<div className="rounded-full bg-accent/20 p-4 mb-4">
									<Plus className="h-6 w-6 text-accent" />
								</div>
								<h3 className="text-xl font-bold text-primary-foreground">
									Create New Event
								</h3>
								<p className="mt-2 text-sm text-secondary">
									Get the party started! Plan your next gathering.
								</p>
							</div>
						</Link>
					</motion.div>

					{/* Event Cards - Only show if there are events */}
					{events[activeTab].map((event) => (
						<Link key={event.id} to={`/event/${event.slug}`}>
							<motion.div
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								className="group relative overflow-hidden rounded-3xl bg-primary/10 backdrop-blur-xl border border-primary/20 hover:border-primary/40 transition-all duration-300"
							>
								<div className="relative aspect-[5/3] w-full">
									{event.coverImage ? (
										<img
											src={event.coverImage}
											alt={event.title}
											className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
										/>
									) : (
										<div className="absolute inset-0 bg-gradient-to-br from-accent/30 via-primary/20 to-primary/30 transition-all duration-300" />
									)}
									<div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/60 to-black" />
									<div className="absolute bottom-0 p-6 w-full">
										{event.rsvpStatus && (
											<div
												className={cn(
													"inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium",
													event.rsvpStatus === "accepted" &&
														"bg-green-500/20 text-green-400 border border-green-500/30",
													event.rsvpStatus === "maybe" &&
														"bg-amber-500/20 text-amber-400 border border-amber-500/30",
													event.rsvpStatus === "declined" &&
														"bg-red-500/20 text-red-400 border border-red-500/30",
													event.rsvpStatus === "pending" &&
														"bg-gray-500/20 text-gray-400 border border-gray-500/30",
												)}
											>
												{event.rsvpStatus === "accepted" && (
													<Check className="w-3 h-3" strokeWidth={2.5} />
												)}
												{event.rsvpStatus === "maybe" && (
													<HelpCircle className="w-3 h-3" strokeWidth={2.5} />
												)}
												{event.rsvpStatus === "declined" && (
													<X className="w-3 h-3" strokeWidth={2.5} />
												)}
												{event.rsvpStatus === "pending" && (
													<Clock className="w-3 h-3" strokeWidth={2.5} />
												)}
												{event.rsvpStatus === "accepted"
													? "Going"
													: event.rsvpStatus === "maybe"
														? "Maybe"
														: event.rsvpStatus === "declined"
															? "Not going"
															: "Awaiting response"}
											</div>
										)}
										<h3 className="text-2xl font-bold text-primary-foreground font-heading tracking-wide">
											{event.title}
										</h3>
										<div className="mt-3 flex items-center gap-2 text-primary-foreground/80">
											<CalendarDays className="h-4 w-4" />
											<p className="text-sm">
												{formatDate(event.startDate)}
												<span className="mx-1.5">•</span>
												<time>{formatTime(event.startDate)}</time>
											</p>
										</div>
										{event.locationAddress && (
											<div className="mt-2 flex flex-wrap">
												<span className="inline-flex items-center px-2.5 py-1 rounded-full bg-accent/10 border border-accent/20 text-sm text-primary-foreground/90">
													<MapPin className="h-3.5 w-3.5 text-accent mr-1.5" />
													{formatLocation(event.locationAddress)}
												</span>
											</div>
										)}

										<div className="mt-4 flex items-center justify-between w-full">
											{event.attendees.length ? (
												<div className="flex -space-x-2">
													{event.attendees.map(
														(attendee: { avatar: string }) => (
															<img
																key={`${event.id}-${attendee.avatar}`}
																src={attendee.avatar}
																alt="attendee"
																className="h-8 w-8 rounded-full border-2 border-black shadow-sm"
															/>
														),
													)}
												</div>
											) : (
												<div />
											)}
											<span className="text-xs bg-accent/20 text-accent border border-accent/30 px-2 py-1 rounded-full font-medium ml-auto">
												{event.status === "hosting"
													? "You're hosting"
													: "You're invited"}
											</span>
										</div>
									</div>
								</div>
							</motion.div>
						</Link>
					))}
				</div>
			</main>
		</div>
	);
}

export const Route = createFileRoute("/_protected/")({
	component: HomeComponent,
	loader: async () => {
		const response = await eventApi.getEvents();

		if (!response.success) {
			throw new Error(response.message);
		}

		return {
			events: response.data,
		};
	},
});
